import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import {
  PANEL_PREDEFINED_COLORS,
  isPredefinedPanelColor,
} from '../../../../shared/panel-predefined-colors';
import { TextEditorComponent, TextEditorStyle } from '../../../../../shared/components/text-editor';
import { ContactLayout1PreviewComponent } from './layouts/layout_1';
import { ContactLayout2PreviewComponent } from './layouts/layout_2';

export interface ContactData {
  title: string;
  subtitle: string;
  titleStyle?: TextEditorStyle;
  map?: {
    embedLink?: string;
    imageUrl?: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  contactInfo?: {
    phone?: string;
    email?: string;
    address?:
      | string
      | {
          street?: string;
          city?: string;
          state?: string;
          zipCode?: string;
          country?: string;
        };
    website?: string;
  };
  socialLinks?: {
    platform: string;
    url: string;
    icon?: string;
    label?: string;
  }[];
  businessHours?: {
    day: string;
    openTime?: string;
    closeTime?: string;
    closed?: boolean;
  }[];
  backgroundColor?: string;
  layout?: 'layout_1' | 'layout_2' | '1' | '2'; // Contact section layout: layout_1=minimal style, layout_2=dark style
}

/**
 * MinimalContactPanelComponent - A compact, editable contact panel used in the website builder for configuring contact information, map, business hours and background styling with live preview layouts.
 * @component
 * @selector app-minimal-contact-panel
 * @example
 *   <app-minimal-contact-panel
 *     [data]="sampleContactData"
 *     [headingColor]="#111111"
 *     [paragraphColor]="'#333333'"
 *     [primaryColor]="'#5E19A1'"
 *     [headingFont]="'Inter'"
 *     [bodyFont]="'Roboto'"
 *     (dataChange)="onContactDataChange($event)"
 *     (closePanel)="onPanelClose()">
 *   </app-minimal-contact-panel>
 *
 * Purpose:
 *   Provides a compact UI panel for editing contact-related content (title, subtitle, map, contact details, social links, business hours)
 *   and appearance (background color, predefined/custom color picker, layout selection). It emits updated data back to the parent
 *   and supports two preview layout formats.
 *
 * Inputs:
 * @input {ContactData} data - The primary contact model containing title, subtitle, map (imageUrl, embedLink, coordinates), contactInfo (phone, email, address, website), socialLinks, businessHours, backgroundColor and optional layout. Defaults are initialized on ngOnInit if missing.
 * @input {string | null} headingColor - Optional override color for headings used by preview components.
 * @input {string | null} paragraphColor - Optional override color for paragraph/body text used by preview components.
 * @input {string | null} primaryColor - Optional primary color used by the previews for accents and buttons.
 * @input {string | null} headingFont - Optional font family for heading text in previews.
 * @input {string | null} bodyFont - Optional font family for body text in previews.
 *
 * Outputs:
 * @output {EventEmitter<ContactData>} dataChange - Emitted whenever the internal ContactData model is changed (deep-copied) so the parent can persist updates.
 * @output {EventEmitter<void>} closePanel - Emitted when the user requests to close the editing panel.
 *
 * Key methods:
 *   - ngOnInit(): Initializes defaults for missing data keys (map, contactInfo, businessHours, layout) and loads custom colors.
 *   - ngOnChanges(changes: SimpleChanges): Reacts to input changes (re-initialize layout default and reload custom color if backgroundColor changes).
 *   - loadCustomColorFromData(): Ensures non-predefined backgroundColor values are stored in customColors.
 *   - getFullAddress(): Normalizes address stored as a string or object and returns a single-line address.
 *   - onFullAddressChange(event): Updates the contactInfo.address as a string and emits change.
 *   - onHeadingChange(value) / onSubtitleChange(value) / onHeadingStyleChange(style): Update text fields and styles then emit change.
 *   - onMapChange() / onContactDetailsChange(): Emit data changes after corresponding edits.
 *   - selectColor(color) / onBackgroundColorChange(color): Set background color and emit change.
 *   - openColorPicker(color?) / closeColorPicker(): Manage color picker state, parse color objects to hex, apply color and maintain customColors list (max 1).
 *   - onColorPickerChange(): Live updates preview color while editing in color picker.
 *   - addCustomColor(color): Adds or replaces the single allowed custom color if not predefined.
 *   - onAddColor(): Convenience method to open the color picker.
 *   - toggleSection(section) / isSectionExpanded(section): Manage expandable panel sections ensuring only one section expanded at a time.
 *   - switchTab(tab) / activeTab state: Toggle between 'content' and 'layout' editing tabs.
 *   - onLayoutChange(layout): Accepts '1'|'2', converts to 'layout_1'|'layout_2' and emits change.
 *   - getSelectedLayout(): Returns currently selected layout as '1' or '2' handling both numeric and 'layout_X' formats.
 *   - getHeadingColor(), getParagraphColor(), getHeadingFont(), getBodyFont(), getPrimaryColor(): Simple getters that provide fallbacks for preview components.
 *   - getContactLayout1Data() / getContactLayout2Data(): Build normalized preview payloads for the two supported preview components.
 *   - emitChange(): Emits a shallow copy of this.data via dataChange.
 *
 * Lifecycle hooks:
 *   - ngOnInit: Ensures required nested objects/arrays and default layout exist and loads any custom background color into customColors.
 *   - ngOnChanges: Watches for changes to the data input to reapply defaults and to reload custom colors when backgroundColor changes.
 */
@Component({
  selector: 'app-minimal-contact-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ColorPickerModule,
    TextEditorComponent,
    ContactLayout1PreviewComponent,
    ContactLayout2PreviewComponent,
  ],
  templateUrl: './contact-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./contact-panel.component.scss'],
})
export class MinimalContactPanelComponent implements OnInit, OnChanges {
  @Input() data: ContactData = {
    title: '',
    subtitle: '',
    map: {
      imageUrl: '',
      coordinates: { lat: 0, lng: 0 },
    },
    contactInfo: {
      phone: '',
      email: '',
      address: '',
      website: '',
    },
    socialLinks: [],
    businessHours: [],
    backgroundColor: '#FFFFFF',
  };

  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;

  @Output() dataChange = new EventEmitter<ContactData>();
  @Output() closePanel = new EventEmitter<void>();

  activeTab: 'content' | 'layout' = 'content';
  expandedSections: Record<string, boolean> = {
    heading: true,
    map: false,
    contactDetails: false,
    background: false,
  };

  predefinedColors: string[] = ['#5E19A1', '#FF9736', '#079271', '#1B5BFF', '#0D0E0F', '#000000'];

  customColors: string[] = [];

  // Color picker state (same as hero panel)
  isColorPickerOpen = false;
  currentPickerColor: string | any = '#000000';

  private static readonly PREDEFINED_COLORS = [...PANEL_PREDEFINED_COLORS];

  /**
   * Ensures the component's contact-related data (map, contactInfo, socialLinks, businessHours, layout) are initialized with sensible defaults and triggers loading of any custom color from saved data.
   * @example
   * gOnInit()
   * undefined
   * @returns {void} Initializes missing contact data fields and loads custom color on component init.
   **/
  ngOnInit(): void {
    // Ensure default values
    if (!this.data.map) {
      this.data.map = { embedLink: '', imageUrl: '', coordinates: { lat: 0, lng: 0 } };
    }
    if (!this.data.contactInfo) {
      this.data.contactInfo = {
        phone: '',
        email: '',
        address: '',
        website: '',
      };
    }
    if (!this.data.socialLinks) {
      this.data.socialLinks = [];
    }
    if (!this.data.businessHours || this.data.businessHours.length === 0) {
      this.data.businessHours = [
        { day: 'Monday - Friday', openTime: '8:00 AM - 6:00 PM', closed: false },
        { day: 'Saturday', openTime: '8:00 AM - 5:00 PM', closed: false },
        { day: 'Sunday', openTime: '9:00 AM - 4:00 PM', closed: false },
      ];
    }

    // Initialize layout if not set (default to layout_1)
    if (!this.data.layout) {
      this.data.layout = 'layout_1';
    }

    // Load custom color on initial load
    this.loadCustomColorFromData();
  }

  // Lifecycle hook to detect when data input changes
  /**
   * Handles input changes for the contact panel: initializes layout to 'layout_1' if missing and triggers loading of a custom background color when present.
   * @example
   * gOnChanges({ data: { layout: undefined, backgroundColor: '#ff0000' } })
   * void
   * @param {{SimpleChanges}} {{changes}} - Angular SimpleChanges object containing changed input properties.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (!this.data.map) {
        this.data.map = { embedLink: '', imageUrl: '', coordinates: { lat: 0, lng: 0 } };
      }
      if (!this.data.contactInfo) {
        this.data.contactInfo = { phone: '', email: '', address: '', website: '' };
      }
      if (!this.data.socialLinks) {
        this.data.socialLinks = [];
      }
      if (!this.data.businessHours || this.data.businessHours.length === 0) {
        this.data.businessHours = [
          { day: 'Monday - Friday', openTime: '8:00 AM - 6:00 PM', closed: false },
          { day: 'Saturday', openTime: '8:00 AM - 5:00 PM', closed: false },
          { day: 'Sunday', openTime: '9:00 AM - 4:00 PM', closed: false },
        ];
      }
      if (!this.data.layout) {
        this.data.layout = 'layout_1';
      }
      if (this.data?.backgroundColor) {
        this.loadCustomColorFromData();
      }
    }
  }

  // Load custom color from data if it's not a predefined color
  /**
   * Load custom background color from component data and update the customColors array accordingly.
   * @example
   * loadCustomColorFromData()
   * // void (no return value)
   * @param {{void}} none - No parameters.
   * @returns {{void}} Updates this.customColors based on this.data.backgroundColor; does not return a value.
   **/
  private loadCustomColorFromData(): void {
    if (!this.data?.backgroundColor) {
      return;
    }

    const bgColor = this.data.backgroundColor;

    // Check if the color is not in predefined colors
    if (!isPredefinedPanelColor(bgColor)) {
      // Add to custom colors (maximum 1 custom color)
      this.customColors = [bgColor];
    } else {
      // If it's a predefined color, clear custom colors
      this.customColors = [];
    }
  }

  /**
   * Returns the contact's full address as a single formatted string, handling both string and object address formats.
   * @example
   * this.etFullAddress()
   * "123 Main St, Springfield, IL, 62704"
   * @param {{void}} {{none}} - No arguments.
   * @returns {{string}} Full address string (joined by commas) or empty string if not available.
   **/
  getFullAddress(): string {
    // Handle both string and object address formats
    if (typeof this.data.contactInfo?.address === 'string') {
      return this.data.contactInfo.address;
    }
    const addr = this.data.contactInfo?.address;
    if (!addr) return '';
    const parts = [addr.street, addr.city, addr.state, addr.zipCode].filter(p => p);
    return parts.join(', ');
  }

  onFullAddressChange(event: any): void {
    const fullAddress = event.target.value;

    // API expects address as a string, so we store it directly
    if (this.data.contactInfo) {
      this.data.contactInfo.address = fullAddress;
    }

    this.onContactDetailsChange();
  }

  onClose(): void {
    this.closePanel.emit();
  }

  switchTab(tab: 'content' | 'layout'): void {
    this.activeTab = tab;
  }

  /**
   * Toggle the expanded/collapsed state of a named panel section; collapse others when expanding.
   * @example
   * toggleSection('contactInfo')
   * undefined
   * @param {{string}} {{section}} - The key/name of the section to toggle.
   * @returns {{void}} No return value.
   **/
  toggleSection(section: string): void {
    const wasExpanded = this.expandedSections[section] || false;

    if (wasExpanded) {
      // If clicking an expanded section, just collapse it
      this.expandedSections[section] = false;
    } else {
      // If clicking a collapsed section, close all others first, then expand this one
      Object.keys(this.expandedSections).forEach(key => {
        this.expandedSections[key] = false;
      });
      this.expandedSections[section] = true;
    }
  }

  isSectionExpanded(section: string): boolean {
    return this.expandedSections[section] || false;
  }

  onHeadingChange(value: string): void {
    this.data.title = value;
    this.emitChange();
  }

  onHeadingStyleChange(style: TextEditorStyle): void {
    this.data.titleStyle = style;
    this.emitChange();
  }

  onSubtitleChange(value: string): void {
    this.data.subtitle = value;
    this.emitChange();
  }

  onMapChange(): void {
    this.emitChange();
  }

  onContactDetailsChange(): void {
    this.emitChange();
  }

  onBackgroundColorChange(color: string): void {
    this.data.backgroundColor = color;
    this.emitChange();
  }

  selectColor(color: string): void {
    this.data.backgroundColor = color;
    this.emitChange();
  }

  openColorPicker(color?: string): void {
    this.currentPickerColor = color || this.data.backgroundColor || '#000000';
    this.isColorPickerOpen = true;
  }

  // Close color picker and add the selected color to custom colors list
  /**
   * Close the color picker, normalize and apply the currently selected color, and add it to custom colors.
   * @example
   * loseColorPicker()
   * undefined
   * @param {void} none - No arguments.
   * @returns {void} No return value; performs side effects (applies the selected background color, adds it to custom colors, and closes the picker).
   **/
  closeColorPicker(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Apply the color immediately
      this.onBackgroundColorChange(hexColor);

      // Add to custom colors (maximum 1 custom color - replace if exists)
      this.addCustomColor(hexColor);
    }
    this.isColorPickerOpen = false;
  }

  // Handle color change from color picker (update preview only, don't add to list yet)
  /**
   * Normalize the picked color to a hex string (ensuring a leading '#'), update currentPickerColor, and apply it for preview via onBackgroundColorChange.
   * @example
   * nColorPickerChange()
   * // undefined
   * @returns {void} No return value; updates component state and triggers a preview color update.
   */
  onColorPickerChange(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Update currentPickerColor to the hex string for consistency
      this.currentPickerColor = hexColor;

      // Apply the color immediately for preview (but don't add to custom colors list yet)
      this.onBackgroundColorChange(hexColor);
    }
  }

  onAddColor(): void {
    // Open color picker modal (same as hero panel)
    this.openColorPicker();
  }

  private addCustomColor(color: string): void {
    // Maximum 1 custom color - replace existing if any
    if (!isPredefinedPanelColor(color)) {
      // Clear existing custom colors and add the new one
      this.customColors = [color];
    }
  }

  private emitChange(): void {
    this.dataChange.emit({ ...this.data });
  }

  /**
   * Handle layout change
   */
  onLayoutChange(layout: '1' | '2'): void {
    // Convert to layout_X format
    const layoutType = `layout_${layout}` as 'layout_1' | 'layout_2';
    this.data.layout = layoutType;
    this.emitChange();
  }

  /**
   * Get selected layout
   */
  getSelectedLayout(): '1' | '2' {
    const layout = this.data.layout;
    if (!layout) return '1';

    // If it's already in layout_X format, extract the number
    if (typeof layout === 'string' && layout.startsWith('layout_')) {
      const num = layout.replace('layout_', '');
      if (/^[12]$/.test(num)) {
        return num as '1' | '2';
      }
    }

    // If it's already a number string, return it
    if (typeof layout === 'string' && /^[12]$/.test(layout)) {
      return layout as '1' | '2';
    }

    return '1';
  }

  /**
   * Get heading color with fallback
   */
  getHeadingColor(): string | null {
    return this.headingColor;
  }

  /**
   * Get paragraph color with fallback
   */
  getParagraphColor(): string | null {
    return this.paragraphColor;
  }

  /**
   * Get heading font with fallback
   */
  getHeadingFont(): string | null {
    return this.headingFont;
  }

  /**
   * Get body font with fallback
   */
  getBodyFont(): string | null {
    return this.bodyFont;
  }

  /**
   * Get primary color with fallback
   */
  getPrimaryColor(): string | null {
    return this.primaryColor;
  }

  /**
   * Get contact layout data for preview
   */
  getContactLayout1Data(): any {
    const address =
      typeof this.data.contactInfo?.address === 'string'
        ? this.data.contactInfo.address
        : this.data.contactInfo?.address
          ? this.getFullAddress()
          : '';

    return {
      title: this.data.title || '',
      subtitle: this.data.subtitle || '',
      address: address,
      phone: this.data.contactInfo?.phone || '',
      email: this.data.contactInfo?.email || '',
      mapEmbedUrl: this.data.map?.embedLink || '',
      mapImage: this.data.map?.imageUrl || '',
      businessHours: this.data.businessHours || [],
      backgroundColor: this.data.backgroundColor || '#FFFFFF',
    };
  }

  /**
   * Get contact layout 2 data for preview
   */
  getContactLayout2Data(): any {
    const address =
      typeof this.data.contactInfo?.address === 'string'
        ? this.data.contactInfo.address
        : this.data.contactInfo?.address
          ? this.getFullAddress()
          : '';

    return {
      title: this.data.title || '',
      subtitle: this.data.subtitle || '',
      address: address,
      phone: this.data.contactInfo?.phone || '',
      email: this.data.contactInfo?.email || '',
      mapEmbedUrl: this.data.map?.embedLink || '',
      mapImage: this.data.map?.imageUrl || '',
      businessHours: this.data.businessHours || [],
      backgroundColor: this.data.backgroundColor || '#FFFFFF',
    };
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
